import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useSearchParams } from "react-router-dom";
import useFetchConsolidatedResults from "../../hooks/useFetchConsolidatedResults";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import {
  downloadSummaryOutputFile,
  downloadDetailedOutputFile,
  getFormatedDate,
  getSanatizedCode,
} from "../../services/utils";
import ConsolidateDataTable from "../../components/DataGrids/ConsolidateDataGrid";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import RunNameDropdown from "../../components/Molecules/RunName/RunNameDropDown";
import CustomDatePicker from "../../components/Molecules/Datepicker/CustomDatePicker";
import dayjs, { Dayjs } from "dayjs";
import { showConsolidateReportSchema } from "../../schemas";
import ReadMoreLess from "../../components/ReadMoreLess";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
} from "../../common/utils/icons";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ScoreBadge from "../../components/Scoring/ScoreBadge";
import ScoreBreakdownDialog from "../../components/Scoring/ScoreBreakdownDialog";
import { getRunScore } from "../../services/scoringService";

// Centralized Filter Configuration for ConsolidateResults
const CONSOLIDATE_RESULTS_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    runName: "Legacy",
    runDate: null, // Will be set to dayjs().subtract(1, "day") in component
    reportType: "summary",
    searchQuery: "",
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["runName", "reportType", "searchQuery"],
    DATE_FIELDS: ["runDate"],
  },
} as const;

const ConsolidateResults = () => {
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();

  // URL search params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Ref to track if we're updating from URL changes to prevent circular updates
  const isUpdatingFromURL = useRef(false);

  const [isloading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [isRunScoreLoading, setIsRunScoreLoading] = useState<boolean>(false);
  const [selectedRunInstance, setSelectedRunInstance] = useState<any>(null);
  const [runScore, setRunScore] = useState<any>(null);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  // Helper function to get initial filter state from URL
  const getInitialFilterState = () => {
    // Use centralized default state
    const defaultFilterState = {
      ...CONSOLIDATE_RESULTS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    const urlFilters: any = {};

    // Handle string fields
    CONSOLIDATE_RESULTS_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS.forEach(
      (field) => {
        const value = searchParams.get(field);
        if (value) {
          urlFilters[field] = value;
        }
      }
    );

    // Handle date fields
    CONSOLIDATE_RESULTS_FILTER_CONFIG.FIELD_TYPES.DATE_FIELDS.forEach(
      (field) => {
        const value = searchParams.get(field);
        if (value) {
          // Validate date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateRegex.test(value)) {
            urlFilters[field] = value;
          }
        }
      }
    );

    // Set default runDate if not provided
    if (!urlFilters.runDate) {
      urlFilters.runDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
    }

    return { ...defaultFilterState, ...urlFilters };
  };

  // Helper function to serialize filter data to URL parameters
  const serializeFiltersToURL = (filters: any) => {
    const newParams = new URLSearchParams(searchParams);

    // Use centralized field configuration
    const fieldsToSerialize = CONSOLIDATE_RESULTS_FILTER_CONFIG.URL_FIELDS;

    // Remove all existing filter parameters first
    fieldsToSerialize.forEach((field) => {
      newParams.delete(field);
    });

    // Add non-empty filter values to URL
    fieldsToSerialize.forEach((field) => {
      const value = filters[field];
      if (value !== null && value !== undefined && value !== "") {
        if (typeof value === "string") {
          newParams.set(field, value);
        } else if (field === "runDate" && dayjs.isDayjs(value)) {
          // Handle dayjs date objects
          newParams.set(field, value.format("YYYY-MM-DD"));
        } else {
          newParams.set(field, String(value));
        }
      }
    });

    return newParams;
  };

  // Initialize filter state from URL
  const initialFilterState = getInitialFilterState();

  const [fileData, setFileData] = useState<any>({
    reports: { items: [], total: 0, page: 0, size: 0, pages: 0 },
    filteredDomains: "",
    runInstance: {},
  });

  const [runName, setRunName] = useState<string | null>(
    initialFilterState.runName
  );
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [reportType, setReportType] = useState<string>(
    initialFilterState.reportType
  );
  const [runDate, setRunDate] = React.useState<Dayjs | null>(
    initialFilterState.runDate
      ? dayjs(initialFilterState.runDate)
      : dayjs().subtract(1, "day")
  );

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [consolidatedReports] = useFetchConsolidatedResults({
    setIsLoading,
    runName,
    page,
    pSize,
    reportType,
    runDate,
  });

  const [searchQuery, setSearchQuery] = useState(
    initialFilterState.searchQuery
  );

  // Update URL when filter state changes (only when not updating from URL)
  useEffect(() => {
    // Skip URL update if we're currently updating from URL changes
    if (isUpdatingFromURL.current) {
      return;
    }

    // Create filter object from current state
    const currentFilters = {
      runName,
      runDate,
      reportType,
      searchQuery,
    };

    // Serialize current filter state to URL parameters
    const newParams = serializeFiltersToURL(currentFilters);

    // Only update URL if the serialized params differ from current URL
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams);
    }
  }, [
    runName,
    runDate,
    reportType,
    searchQuery,
    searchParams,
    setSearchParams,
  ]);

  // Update filter state when URL changes (for back/forward navigation)
  useEffect(() => {
    // Skip if we're currently updating from URL changes to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Extract current filter values from URL
    const urlFilterState = getInitialFilterState();

    // Check if URL filter state differs from current filter state
    const currentFilters = {
      runName,
      runDate: runDate?.format("YYYY-MM-DD"),
      reportType,
      searchQuery,
    };
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = currentFilters[key as keyof typeof currentFilters];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? null
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? null
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Update filter state if URL has different values
    if (hasFilterChanges) {
      // Set flag to prevent URL updates while we update state
      isUpdatingFromURL.current = true;

      setRunName(urlFilterState.runName);
      setReportType(urlFilterState.reportType);
      setSearchQuery(urlFilterState.searchQuery);
      if (urlFilterState.runDate) {
        setRunDate(dayjs(urlFilterState.runDate));
      }

      // Reset flag after state update is processed
      setTimeout(() => {
        isUpdatingFromURL.current = false;
      }, 100);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  useEffect(() => {
    setFileData((prevFormData: any) => ({
      ...prevFormData,
      reports: consolidatedReports,
    }));
  }, [consolidatedReports]);

  const selectedRunId = useMemo(() => {
    return selectedRunInstance?.id || selectedRunInstance?.run_id || null;
  }, [selectedRunInstance]);

  useEffect(() => {
    if (!selectedRunId || selectedRunId <= 0) {
      setRunScore(null);
      return;
    }

    const fetchRunScore = async () => {
      setIsRunScoreLoading(true);
      try {
        const result = await getRunScore(selectedRunId);
        setRunScore(result || null);
      } catch (error) {
        setRunScore(null);
      } finally {
        setIsRunScoreLoading(false);
      }
    };

    fetchRunScore();
  }, [selectedRunId]);

  /**
   * Set table coloums for execution history
   */
  const summaryColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "Rule Name",
      flex: 1,
      // renderCell: (params: any) => {
      //   return <LimitChractersWithTooltip value={params?.value} />;
      // },
    },
    {
      field: "description",
      headerName: "Description",
      width: 120,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "execution_time",
      headerName: "Execution Time",
      flex: 1,
      renderCell: (params) => {
        const formattedDate = getFormatedDate(params.row.execution_time);
        return (
          <div>
            <div>{formattedDate}</div>
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <>
            {params?.value ? (
              <Box title="Success">
                <IconCheckCircleIconGreenSvg width={21} height={21} />
              </Box>
            ) : (
              <Box title="Fail">
                <IconCrossCircleIconSvg width={21} height={21} />
              </Box>
            )}
          </>
        );
      },
    },
    {
      field: "records",
      headerName: "Records",
      flex: 1,
    },
    {
      field: "mismatched",
      headerName: "Mismatched",
      flex: 1,
    },
    {
      field: "missing",
      headerName: "Missing",
      flex: 1,
    },
  ];

  const detailColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "Rule Name",
      flex: 1,
      // renderCell: (params: any) => {
      //   return <span style={{ cursor: "pointer" }}>{params.value}</span>;
      // },
    },
    {
      field: "description",
      headerName: "Description",
      width: 120,
      flex: 1,
    },
    {
      field: "execution_time",
      headerName: "Execution Time",
      flex: 1,
      renderCell: (params) => {
        const formattedDate = getFormatedDate(params.row.execution_time);
        return (
          <div>
            <div>{formattedDate}</div>
          </div>
        );
      },
    },
    {
      field: "check_name",
      headerName: "Check Name",
      flex: 1,
    },
    {
      field: "column_name",
      headerName: "Column Name",
      flex: 1,
      renderCell: (params) => {
        const sanatizedColumnName = getSanatizedCode(params.row.column_name);
        return (
          <Box sx={{ width: "100%" }}>
            <div>{sanatizedColumnName}</div>
          </Box>
        );
      },
    },
    {
      field: "key_names",
      headerName: "Key Name",
      flex: 1,
      renderCell: (params) => {
        const sanatizedKeyName = getSanatizedCode(params.row.key_names);
        return (
          <Box sx={{ width: "100%" }}>
            <div>{sanatizedKeyName}</div>
          </Box>
        );
      },
    },
    {
      field: "column_values",
      headerName: "Column Values",
      flex: 1,
      renderCell: (params) => {
        const sanatizedColumnValues = getSanatizedCode(
          params.row.column_values
        );
        return (
          <Box sx={{ width: "100%" }}>
            <div>
              {sanatizedColumnValues && sanatizedColumnValues.length > 100 ? (
                <ReadMoreLess data={sanatizedColumnValues} />
              ) : (
                sanatizedColumnValues
              )}
            </div>
          </Box>
        );
      },
    },
  ];

  const handleSearchInputChange = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearchQuery(e.target.value);
  };
  const handleSearchSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    // Filter domains based on the searchQuery
    const filteredReports = fileData?.items?.filter((report: any) => {
      return report.name.toLowerCase().includes(searchQuery.toLowerCase());
    });
    //You can set the filtered domains as a result in your component state
    setFileData((prevFormData: any) => ({
      ...prevFormData,
      reports: filteredReports,
    }));
  };

  const validateField = async (name: any, value: any) => {
    try {
      const partialReferenceData = { ...fileData, [name]: value };
      await showConsolidateReportSchema.validateAt(name, partialReferenceData);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const handleChangeRunName = (e: any, value: any) => {
    setSelectedRunInstance(value || null);
    setFileData((prevFormData: any) => ({
      ...prevFormData,
      runInstance: value,
    }));
    setRunName(value?.run_name || null);
    validateField("run_name", value?.run_name);
  };

  const handleChangeRunDate = (value: any) => {
    setRunDate(value);
    setFileData((prevFormData: any) => ({
      ...prevFormData,
      runInstance: value,
    }));
  };

  const filteredData = useMemo(() => {
    return fileData?.reports?.items?.filter((report: any, index: number) => {
      report.id = index;
      return report.name.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [fileData?.reports?.items, searchQuery]);
  const runDateToString = (runDate ?? dayjs().subtract(1, "day")).format(
    "YYYY-MM-DD"
  );

  return (
    <Box>
      {(isloading || isDownloadLoading) && (
        <Loader isLoading={isloading || isDownloadLoading} />
      )}
      <Box className="text-box-card list-page-card">
        <Grid container rowSpacing={3} columnSpacing={3}>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <RunNameDropdown
              setIsLoading={setIsLoading}
              handleChangeRunName={handleChangeRunName}
              currentRunName={runName}
              onResolvedRunInstance={setSelectedRunInstance}
              className="form-control-autocomplete dropdown-padding-1"
              name="run_name"
              required={true}
              error={!!errors.run_name}
              helperText={errors.run_name || ""}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <label className="label-text">
              Run Date<span className="required-asterisk">*</span>
            </label>

            <CustomDatePicker
              value={runDate}
              onChange={(newValue) => handleChangeRunDate(newValue)}
            />
          </Grid>
          <Grid item xs={9} md={3}>
            <label className="label-text">
              Report Type<span className="required-asterisk">*</span>
            </label>
            <Select
              required
              labelId="report-type-label"
              id="report-type"
              value={reportType}
              displayEmpty
              className="form-control-1 alternative-1"
              onChange={(e) => {
                setReportType(e.target.value);
              }}
              name="reportType"
            >
              <MenuItem value="" disabled>
                Select...
              </MenuItem>
              <MenuItem value="detail">Detail</MenuItem>
              <MenuItem value="summary">Summary</MenuItem>
            </Select>
            <span className="validation-error ml-0">
              {errors.report_type && <span>{errors.report_type}</span>}
            </span>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <label className="label-text">Search Rule</label>
            <form className="common-search-panel">
              <InputBase
                placeholder="Search by Rule Name"
                className="search-textbox"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                  }
                }}
              />
              <IconButton className="search-icon">
                <Search className="svg_icons" />
              </IconButton>
            </form>
          </Grid>
        </Grid>

        {(isRunScoreLoading || runScore) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              mt: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#44546F" }}
            >
              Run Data Quality Score
            </Typography>
            {isRunScoreLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading score...
                </Typography>
              </Box>
            ) : (
              runScore && (
                <ScoreBadge
                  score={runScore.score}
                  size="medium"
                  onClick={() => setScoreDialogOpen(true)}
                  tooltipText="Click to view run score breakdown"
                />
              )
            )}
          </Box>
        )}
      </Box>

      <ScoreBreakdownDialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        type="run"
        scoreData={runScore}
      />

      <ConsolidateDataTable
        dataColumns={
          (reportType === "summary" ? summaryColumns : detailColumns) || []
        }
        dataRows={filteredData}
        loading={isloading}
        dataListTitle={"Comparison Summary List"}
        isExportButtonRequired={true}
        className="dataTable no-radius hide-progress-icon"
        checkboxSelection={false}
        rowCount={fileData?.reports?.total || 0}
        paginationModel={{
          page: page - 1,
          pageSize: pSize,
        }}
        onPaginationModelChange={(params: any) => {
          if (params.pageSize !== pSize || params.page !== page - 1) {
            setPage(params.page + 1);
            setPSize(params.pageSize);
          }
        }}
        downloadFromAPIFile={() =>
          reportType === "summary"
            ? downloadSummaryOutputFile(
                runName,
                runDateToString,
                setIsDownloadLoading
              )
            : downloadDetailedOutputFile(
                runName,
                runDateToString,
                setIsDownloadLoading
              )
        }
        singlePageMaxHeightDiff={368}
      />
    </Box>
  );
};

export default ConsolidateResults;
